import React, { useEffect, useState } from "react";
import { apiGet, apiPost, apiDelete, formatDateTime, formatDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { FileText, Download, RefreshCcw, Search, ChevronRight, Loader2, Sparkles, Trash2, Eye } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import SectorAnalysisCard from "@/components/admin/SectorAnalysisCard";

/**
 * Admin Report Monitor — surfaces:
 *   * stats card (total / analysed / by type / companies covered)
 *   * "Discover all" / "Analyse pending" buttons
 *   * scrollable list of the most recent reports with company link, type,
 *     analysis status, file size, source URL.
 */
const ReportMonitorPage = () => {
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState({ discover: false, analyse: false, reanalyse: false });
  const [perCompany, setPerCompany] = useState("");
  const [perBusy, setPerBusy] = useState(false);
  const [reanalyseProgress, setReanalyseProgress] = useState(null);
  // Fix 5 — Delete confirmation dialog state
  const [deleteTarget, setDeleteTarget] = useState(null); // {report_id, title}
  const [deleting, setDeleting] = useState(false);
  // Re-analyse spinner per-row
  const [reanalysingId, setReanalysingId] = useState(null);
  const [reanalyseMode, setReanalyseMode] = useState("missing_only");
  // Phase 7 Stage 6 — bulk rationale generator
  const [rationaleProgress, setRationaleProgress] = useState(null);
  const [rationaleBusy, setRationaleBusy] = useState(false);

  // Cost-Optimisation #4 — analyse new only flow
  const [analyseNewPreview, setAnalyseNewPreview] = useState(null); // {new_report_count, preview, estimate}
  const [analyseNewOpen, setAnalyseNewOpen] = useState(false);
  const [analyseNewBusy, setAnalyseNewBusy] = useState(false);

  const openAnalyseNewDialog = async () => {
    setAnalyseNewBusy(true);
    try {
      const res = await apiGet("/admin/reports/analyse-new/preview?limit=200");
      setAnalyseNewPreview(res);
      setAnalyseNewOpen(true);
    } catch (e) {
      toast.error("Could not estimate cost", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAnalyseNewBusy(false);
    }
  };

  const confirmAnalyseNew = async () => {
    setAnalyseNewBusy(true);
    try {
      const res = await apiPost("/admin/reports/analyse-new", {});
      if (res.status === "no_new_reports") {
        toast.info("Nothing to analyse — all reports are already up to date.");
      } else {
        toast.success(`Analyse-new started`, {
          description: `${res.queued} report${res.queued === 1 ? "" : "s"} queued · est. $${res.estimate?.total_usd?.toFixed?.(2) ?? "?"}`,
          duration: 9000,
        });
      }
      setAnalyseNewOpen(false);
      load();
    } catch (e) {
      toast.error("Failed to start analysis", { description: e?.response?.data?.detail || e.message });
    } finally {
      setAnalyseNewBusy(false);
    }
  };

  const load = () => {
    apiGet("/admin/reports/stats").then(setStats).catch(() => {});
    apiGet("/admin/reports/list?limit=200").then(setReports).catch(() => setReports([]));
    apiGet("/admin/reports/reanalyse-progress").then(setReanalyseProgress).catch(() => {});
    apiGet("/admin/rationale/run-progress").then(setRationaleProgress).catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, []);

  // Faster polling when bulk re-analysis is running
  useEffect(() => {
    if (!reanalyseProgress?.running) return undefined;
    const id = setInterval(() => {
      apiGet("/admin/reports/reanalyse-progress").then(setReanalyseProgress).catch(() => {});
    }, 3000);
    return () => clearInterval(id);
  }, [reanalyseProgress?.running]);

  // Faster polling when bulk rationale generation is running
  useEffect(() => {
    if (!rationaleProgress?.running) return undefined;
    const id = setInterval(() => {
      apiGet("/admin/rationale/run-progress").then(setRationaleProgress).catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [rationaleProgress?.running]);

  const triggerRationaleRunAll = async () => {
    setRationaleBusy(true);
    try {
      const res = await apiPost("/admin/rationale/run-all", {
        skip_fresh: true,
        concurrency: 1,
      });
      if (res.status === "already_running") {
        toast.info("Rationale generation already in progress", {
          description: `${res.done}/${res.total} stocks complete`,
        });
      } else {
        toast.success("Investment Rationale generation started", {
          description: `Walking ${res.total} stocks in priority order (BUY → WATCH high → WATCH low → HOLD).`,
          duration: 9000,
        });
      }
      setRationaleProgress(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start rationale generation");
    } finally {
      setRationaleBusy(false);
    }
  };

  const triggerDiscoverAll = async () => {
    setBusy((s) => ({ ...s, discover: true }));
    try {
      await apiPost("/admin/reports/discover-all");
      toast.success("CSE reports discovery started", {
        description: "Walking ~287 companies on cse.lk. Polling for results — refresh in a few minutes.",
        duration: 9000,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Discovery failed");
    } finally { setBusy((s) => ({ ...s, discover: false })); }
  };

  const triggerSingleCompany = async () => {
    if (!perCompany.trim()) return;
    setPerBusy(true);
    try {
      const res = await apiPost(`/admin/reports/discover/${perCompany.trim().toUpperCase()}`);
      toast.success(`${res.company_name || perCompany}: ${res.new_downloaded} new, ${res.skipped_existing} skipped`, {
        description: res.failures?.length ? `${res.failures.length} failure(s)` : "",
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setPerBusy(false); }
  };

  const triggerReanalyseAll = async (overrideMode) => {
    const useMode = overrideMode || reanalyseMode;
    setBusy((s) => ({ ...s, reanalyse: true }));
    try {
      const res = await apiPost(`/admin/reports/reanalyse-all?mode=${useMode}`);
      if (res.status === "already_running") {
        toast.info("Re-analysis already in progress", {
          description: `${res.done}/${res.total} reports complete`,
        });
      } else {
        toast.success(`Bulk re-analysis started (${useMode})`, {
          description: "Re-running selected reports through the new multi-year Claude prompt.",
          duration: 9000,
        });
      }
      setReanalyseProgress(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-analysis failed to start");
    } finally { setBusy((s) => ({ ...s, reanalyse: false })); }
  };

  // Fix 5 — re-analyse a SINGLE report (admin only)
  const reanalyseOne = async (report_id) => {
    setReanalysingId(report_id);
    try {
      await apiPost(`/reports/${report_id}/analyse`, {});
      toast.success("Re-analysis started — refresh in ~30s for results");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-analysis failed");
    } finally {
      setReanalysingId(null);
    }
  };

  // Fix 5 — confirm + perform delete (admin only)
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiDelete(`/reports/${deleteTarget.report_id}`);
      toast.success("Report deleted successfully");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const filtered = reports.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (r.company_code || "").toLowerCase().includes(q) ||
      (r.company_name || "").toLowerCase().includes(q) ||
      (r.title || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-6" data-testid="report-monitor-page">
      <div>
        <h1 className="font-display text-xl font-semibold flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Report Monitor
        </h1>
        <p className="text-sm text-muted-foreground">
          Auto-discovered annual & interim reports from cse.lk with Claude-extracted fundamentals.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total reports" value={stats?.total_reports ?? "—"} testId="stat-total" />
        <Stat label="Analysed" value={stats?.analysed_reports ?? "—"} testId="stat-analysed" />
        <Stat label="Pending analysis" value={stats?.pending_reports ?? "—"} testId="stat-pending" />
        <Stat label="Companies covered" value={stats?.companies_with_reports ?? "—"} testId="stat-companies" />
      </div>

      {/* Layer 6 — Sector Analysis status */}
      <SectorAnalysisCard />

      {/* Controls */}
      <Card className="border bg-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base">Trigger discovery</CardTitle>
          <CardDescription>
            "Discover all" walks every active company (~287) on cse.lk and downloads new PDFs.
            Auto-runs daily at 6:30pm with Claude analysis at 8:00pm.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={triggerDiscoverAll} disabled={busy.discover} className="gap-2" data-testid="discover-all-button">
              {busy.discover ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              Discover all (~3-5 min)
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              <Input
                value={perCompany}
                onChange={(e) => setPerCompany(e.target.value)}
                placeholder="Company code (e.g. COMB)"
                className="h-9 w-40"
                data-testid="single-company-input"
              />
              <Button variant="outline" onClick={triggerSingleCompany} disabled={perBusy || !perCompany.trim()} className="gap-2" data-testid="single-company-discover">
                {perBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
                Fetch one
              </Button>
            </div>
          </div>
          {stats?.discovery_status?.completed_at && (
            <p className="text-[11px] text-muted-foreground">
              Last full discovery: {formatDateTime(stats.discovery_status.completed_at)} —{" "}
              {stats.discovery_status.new_reports_downloaded || 0} new reports across{" "}
              {stats.discovery_status.companies_checked || 0} companies.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Cost-Optimisation #4 — Analyse new reports only (with cost preview) */}
      <Card className="border bg-card" data-testid="analyse-new-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(var(--success))]" />
            Analyse new reports only
          </CardTitle>
          <CardDescription>
            Skips any report where <code className="text-[11px] bg-muted px-1 py-0.5 rounded">analysis_complete=true</code> —
            already-analysed PDFs are never re-processed (unless you click
            "Re-analyse" in the row). Shows the estimated Claude cost
            before you commit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={openAnalyseNewDialog}
              disabled={analyseNewBusy}
              className="gap-2"
              data-testid="analyse-new-button"
            >
              {analyseNewBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Analyse new reports only
            </Button>
            {stats?.pending_reports != null && (
              <span className="text-xs text-muted-foreground">
                {stats.pending_reports} report{stats.pending_reports === 1 ? "" : "s"} pending analysis.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bulk re-analysis (Phase 7 Stage 1) */}
      <Card className="border bg-card" data-testid="reanalyse-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Multi-year fundamentals re-analysis
          </CardTitle>
          <CardDescription>
            Re-runs every existing report through the upgraded 5-part Claude prompt to
            populate the <code className="text-[11px] bg-muted px-1 py-0.5 rounded">historical_fundamentals</code> collection
            (multi-year EPS, revenue, ROE, dividends, book value). Powers Layer 2 CAGR scoring
            and the lumpy-earnings detector.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Annual-only one-click action — recommended monthly */}
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="reanalyse-annual-block">
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1">
                <div className="text-sm font-medium flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Re-analyse Annual Reports Only
                </div>
                <p className="text-[11px] text-muted-foreground leading-snug">
                  Recommended: run monthly after CSE companies file their annual results.
                  Extracts multi-year financial history for Layer 2 scoring improvement.
                  Approx. LLM cost: <span className="tabular">$2-3</span>.
                </p>
              </div>
              <Button
                onClick={() => triggerReanalyseAll("annual_only")}
                disabled={busy.reanalyse || reanalyseProgress?.running}
                size="sm"
                className="gap-2 shrink-0"
                data-testid="reanalyse-annual-button"
              >
                {(busy.reanalyse || reanalyseProgress?.running) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCcw className="h-3.5 w-3.5" />
                )}
                Run annual-only
              </Button>
            </div>
          </div>

          {/* Flexible mode picker for full / missing-only runs */}
          <div className="space-y-2">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Custom run
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={reanalyseMode}
                onChange={(e) => setReanalyseMode(e.target.value)}
                disabled={busy.reanalyse || reanalyseProgress?.running}
                className="h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                data-testid="reanalyse-mode-select"
              >
                <option value="missing_only">Missing only — companies lacking 3+ years</option>
                <option value="annual_only">Annual reports only — best history yield</option>
                <option value="all">All reports — most thorough (highest LLM cost)</option>
              </select>
              <Button
                onClick={() => triggerReanalyseAll()}
                disabled={busy.reanalyse || reanalyseProgress?.running}
                variant="outline"
                className="gap-2"
                data-testid="reanalyse-all-button"
              >
                {(busy.reanalyse || reanalyseProgress?.running) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCcw className="h-3.5 w-3.5" />
                )}
                {reanalyseProgress?.running ? "Re-analysing…" : "Run re-analysis"}
              </Button>
              {stats?.total_reports != null && (
                <span className="text-xs text-muted-foreground ml-auto">
                  {stats.total_reports} report{stats.total_reports === 1 ? "" : "s"} on disk
                </span>
              )}
            </div>
          </div>

          {reanalyseProgress && (reanalyseProgress.running || reanalyseProgress.done > 0) && (
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2" data-testid="reanalyse-progress">
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="font-medium">
                  {reanalyseProgress.running ? "In progress" : "Last run complete"}
                  {reanalyseProgress.mode && (
                    <Badge variant="outline" className="ml-2 text-[10px] uppercase tracking-wide">
                      {reanalyseProgress.mode.replace(/_/g, " ")}
                    </Badge>
                  )}
                  {reanalyseProgress.current_company_code && (
                    <span className="text-muted-foreground ml-2">
                      · processing {reanalyseProgress.current_company_code}
                    </span>
                  )}
                </span>
                <span className="tabular text-muted-foreground">
                  {reanalyseProgress.done} / {reanalyseProgress.total} ({reanalyseProgress.percent}%)
                </span>
              </div>
              <Progress value={reanalyseProgress.percent || 0} className="h-2" />
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                  {reanalyseProgress.succeeded} succeeded
                </Badge>
                {reanalyseProgress.failed > 0 && (
                  <Badge variant="outline" className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                    {reanalyseProgress.failed} failed
                  </Badge>
                )}
                {reanalyseProgress.started_at && (
                  <span className="text-muted-foreground">
                    started {formatDateTime(reanalyseProgress.started_at)}
                  </span>
                )}
                {reanalyseProgress.ended_at && !reanalyseProgress.running && (
                  <span className="text-muted-foreground">
                    finished {formatDateTime(reanalyseProgress.ended_at)}
                  </span>
                )}
              </div>
              {reanalyseProgress.errors?.length > 0 && (
                <details className="text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer hover:text-foreground">
                    Show last {reanalyseProgress.errors.length} error{reanalyseProgress.errors.length === 1 ? "" : "s"}
                  </summary>
                  <ul className="mt-1 space-y-0.5 pl-4 list-disc">
                    {reanalyseProgress.errors.map((err, i) => (
                      <li key={i}>
                        <span className="font-mono">{err.company_code || "?"}</span>: {err.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Investment Rationale generation (Phase 7 Stage 6) */}
      <Card className="border bg-card" data-testid="rationale-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-display text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            Investment Rationale generation
          </CardTitle>
          <CardDescription>
            Generates plain-English investment rationales (✅ supporting points + ⚠ concerns + bottom line)
            for every analysed CSE security via <span className="font-mono text-[11px] bg-muted px-1 py-0.5 rounded">claude-haiku-4-5</span>.
            Runs sequentially in priority order (BUY → WATCH ≥55 → WATCH &lt;55 → HOLD). Idempotent —
            already-fresh rationales are skipped automatically. Approx. cost: ~US$1-2 for the full ~260-stock pass.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={triggerRationaleRunAll}
              disabled={rationaleBusy || rationaleProgress?.running}
              className="gap-2"
              data-testid="rationale-run-all-button"
            >
              {(rationaleBusy || rationaleProgress?.running) ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              {rationaleProgress?.running ? "Generating…" : "Generate rationales for all stocks"}
            </Button>
            {rationaleProgress?.estimated_cost_usd != null && (
              <span className="ml-auto text-xs text-muted-foreground tabular">
                Est. spend so far:{" "}
                <span className="font-medium text-foreground">
                  US${(rationaleProgress.estimated_cost_usd || 0).toFixed(4)}
                </span>
                {" · "}
                {(((rationaleProgress.input_tokens || 0) + (rationaleProgress.output_tokens || 0)) / 1000).toFixed(1)}K tokens
              </span>
            )}
          </div>

          {rationaleProgress && (rationaleProgress.running || rationaleProgress.done > 0) && (
            <div className="rounded-lg border bg-muted/20 p-3 space-y-2" data-testid="rationale-progress">
              <div className="flex items-center justify-between gap-3 text-xs">
                <span className="font-medium">
                  {rationaleProgress.running ? "In progress" : "Last run complete"}
                  {rationaleProgress.current_symbol && (
                    <span className="text-muted-foreground ml-2">
                      · processing <span className="font-mono">{rationaleProgress.current_symbol}</span>
                    </span>
                  )}
                </span>
                <span className="tabular text-muted-foreground">
                  Generating rationales: {rationaleProgress.done} / {rationaleProgress.total} ({rationaleProgress.percent}%)
                </span>
              </div>
              <Progress value={rationaleProgress.percent || 0} className="h-2" />
              <div className="flex flex-wrap gap-2 text-[11px]">
                <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30">
                  {rationaleProgress.succeeded} generated
                </Badge>
                <Badge variant="outline" className="bg-muted text-muted-foreground">
                  {rationaleProgress.skipped_fresh} skipped (already fresh)
                </Badge>
                {rationaleProgress.failed > 0 && (
                  <Badge variant="outline" className="bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30">
                    {rationaleProgress.failed} failed
                  </Badge>
                )}
                {rationaleProgress.by_bucket && (
                  <span className="text-muted-foreground">
                    BUY {rationaleProgress.by_bucket.buy || 0} ·
                    WATCH-high {rationaleProgress.by_bucket.watch_high || 0} ·
                    WATCH-low {rationaleProgress.by_bucket.watch_low || 0} ·
                    HOLD {rationaleProgress.by_bucket.hold || 0}
                  </span>
                )}
                {rationaleProgress.started_at && (
                  <span className="text-muted-foreground ml-auto">
                    started {formatDateTime(rationaleProgress.started_at)}
                  </span>
                )}
                {rationaleProgress.ended_at && !rationaleProgress.running && (
                  <span className="text-muted-foreground">
                    finished {formatDateTime(rationaleProgress.ended_at)}
                  </span>
                )}
              </div>
              {rationaleProgress.errors?.length > 0 && (
                <details className="text-[11px] text-muted-foreground">
                  <summary className="cursor-pointer hover:text-foreground">
                    Show last {rationaleProgress.errors.length} error{rationaleProgress.errors.length === 1 ? "" : "s"}
                  </summary>
                  <ul className="mt-1 space-y-0.5 pl-4 list-disc">
                    {rationaleProgress.errors.map((err, i) => (
                      <li key={i}>
                        <span className="font-mono">{err.cse_symbol || "?"}</span>: {err.error}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reports list */}
      <Card className="border bg-card overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="font-display text-base">Recent reports</CardTitle>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by company or title…"
                className="h-8 w-64 pl-7"
                data-testid="reports-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 border-b">
                <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-2.5 text-left">Company</th>
                  <th className="px-4 py-2.5 text-left">Title</th>
                  <th className="px-4 py-2.5 text-left">Type</th>
                  <th className="px-4 py-2.5 text-left">Period</th>
                  <th className="px-4 py-2.5 text-left">Downloaded</th>
                  <th className="px-4 py-2.5 text-right">Size</th>
                  <th className="px-4 py-2.5 text-left">Analysed</th>
                  <th className="px-4 py-2.5 text-left">Source</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-sm text-muted-foreground">
                      No reports yet. Click "Discover all" or run the per-company button to pull from cse.lk.
                    </td>
                  </tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.report_id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2.5">
                      <Link to={`/stocks/${r.company_code}`} className="text-primary hover:underline inline-flex items-center gap-1">
                        {r.company_code}
                        <ChevronRight className="h-3 w-3" />
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 max-w-[420px] truncate" title={r.title}>
                      {r.title || r.file_name}
                    </td>
                    <td className="px-4 py-2.5">
                      <Badge variant="secondary" className="capitalize text-[10px]">{r.report_type || "other"}</Badge>
                    </td>
                    <td className="px-4 py-2.5 text-xs" data-testid={`report-period-${r.report_id}`}>
                      <span className="font-medium">
                        {r.report_period || (r.report_date ? formatDate(r.report_date) : "—")}
                      </span>
                      <div className="text-[10px] text-muted-foreground">financial period</div>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-muted-foreground" data-testid={`report-downloaded-${r.report_id}`}>
                      {r.created_at ? formatDateTime(r.created_at) : r.discovered_at ? formatDateTime(r.discovered_at) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular text-xs">
                      {r.size_bytes ? `${(r.size_bytes / 1024 / 1024).toFixed(1)} MB` : "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      {r.is_analysed ? (
                        <Badge variant="outline" className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/30 text-[10px]">
                          analysed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40 text-[10px]">
                          pending
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs">
                      <Badge variant="outline" className="text-[10px]">
                        {r.discovery_source || "manual"}
                      </Badge>
                    </td>
                    {/* Fix 5 — Per-row actions: View · Re-analyse · Delete */}
                    <td className="px-4 py-2.5">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/file/${r.report_id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground"
                          title="View / Download PDF"
                          data-testid={`monitor-view-${r.report_id}`}
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </a>
                        <button
                          type="button"
                          onClick={() => reanalyseOne(r.report_id)}
                          disabled={reanalysingId === r.report_id}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground disabled:opacity-50"
                          title="Re-analyse with Claude"
                          data-testid={`monitor-reanalyse-${r.report_id}`}
                        >
                          {reanalysingId === r.report_id
                            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            : <RefreshCcw className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget({ report_id: r.report_id, title: r.title || r.file_name || r.report_period || "Report" })}
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-rose-500/10 text-rose-600 dark:text-rose-400"
                          title="Delete report"
                          data-testid={`monitor-delete-${r.report_id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Cost-Optimisation #4 — Analyse-new confirmation dialog */}
      <Dialog open={analyseNewOpen} onOpenChange={setAnalyseNewOpen}>
        <DialogContent className="sm:max-w-md" data-testid="analyse-new-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[hsl(var(--success))]" />
              Analyse new reports only
            </DialogTitle>
            <DialogDescription>
              Already-analysed reports will be skipped to save Claude tokens.
            </DialogDescription>
          </DialogHeader>
          {analyseNewPreview && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-md border bg-muted/40 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    New reports found
                  </div>
                  <div
                    className="mt-1 text-2xl font-display font-semibold tabular"
                    data-testid="analyse-new-count"
                  >
                    {analyseNewPreview.new_report_count}
                  </div>
                </div>
                <div className="rounded-md border bg-[hsl(var(--success))]/10 border-[hsl(var(--success))]/30 p-3">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
                    Estimated cost
                  </div>
                  <div
                    className="mt-1 text-2xl font-display font-semibold tabular text-[hsl(var(--success))]"
                    data-testid="analyse-new-cost"
                  >
                    ${(analyseNewPreview.estimate?.total_usd ?? 0).toFixed(2)}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    ≈ ${(analyseNewPreview.estimate?.per_report_usd ?? 0).toFixed(3)}/report
                  </div>
                </div>
              </div>
              {analyseNewPreview.preview?.length > 0 && (
                <div className="rounded-md border max-h-44 overflow-y-auto text-xs">
                  <div className="px-3 py-1.5 bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                    First {Math.min(25, analyseNewPreview.preview.length)} reports
                  </div>
                  <ul className="divide-y">
                    {analyseNewPreview.preview.slice(0, 25).map((r) => (
                      <li key={r.report_id} className="px-3 py-1.5 flex items-center justify-between gap-2">
                        <span className="font-mono text-[11px] shrink-0 text-muted-foreground">
                          {r.company_code}
                        </span>
                        <span className="truncate flex-1">{r.title || r.file_name}</span>
                        <Badge variant="secondary" className="capitalize text-[10px] shrink-0">
                          {r.report_type || "other"}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground italic">
                Model: {analyseNewPreview.estimate?.model || "—"} ·
                avg ~{analyseNewPreview.estimate?.avg_input_tokens_per_report?.toLocaleString?.() ?? "?"} input                / ~{analyseNewPreview.estimate?.avg_output_tokens_per_report?.toLocaleString?.() ?? "?"} output tokens per report.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setAnalyseNewOpen(false)}
              disabled={analyseNewBusy}
              data-testid="analyse-new-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmAnalyseNew}
              disabled={analyseNewBusy || (analyseNewPreview?.new_report_count ?? 0) === 0}
              className="gap-2"
              data-testid="analyse-new-confirm"
            >
              {analyseNewBusy ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Sparkles className="h-3.5 w-3.5" />
              )}
              Analyse now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix 5 — Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent data-testid="monitor-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete this report?</DialogTitle>
            <DialogDescription className="pt-2 space-y-2">
              <span className="block font-medium text-foreground">{deleteTarget?.title}</span>
              <span className="block text-rose-600 dark:text-rose-400">This cannot be undone — the PDF, extracted analysis, and all linked data will be permanently removed.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting} data-testid="monitor-delete-cancel">Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting} data-testid="monitor-delete-confirm">
              {deleting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Stat = ({ label, value, testId }) => (
  <div className="rounded-lg border bg-card p-3" data-testid={testId}>
    <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-1 text-2xl font-display font-semibold tabular">{value}</div>
  </div>
);

export default ReportMonitorPage;
